// Mirrors GPMetals.Core.Models.ListingRefresh.* on the API (ListingRefreshController).

export interface IListingRefreshOptions {
  regenerateTitles: boolean;
  regenerateDescriptions: boolean;
  applyIntelligencePricing: boolean;
  applyImageSettings: boolean;
  lookupKType: boolean;
  publishToEbay: boolean;
  publishToPartPro: boolean;
  publishPerCycle: number;
  cycleMinutes: number;
  enrichmentInFlight: number;
  legacySource?: string | null;
  partIds?: string[];
}

export const DEFAULT_LISTING_REFRESH_OPTIONS: IListingRefreshOptions = {
  regenerateTitles: true,
  regenerateDescriptions: true,
  applyIntelligencePricing: true,
  applyImageSettings: true,
  lookupKType: true,
  publishToEbay: true,
  publishToPartPro: true,
  publishPerCycle: 100,
  cycleMinutes: 15,
  enrichmentInFlight: 100,
  legacySource: null,
};

export interface IListingRefreshPreviewStates {
  noCategory: number;
  noImages: number;
  noPrice: number;
  missingPolicies: number;
  missingLocation: number;
  readyForEnrichment: number;
}

export interface IListingRefreshPreviewImageSettings {
  configured: boolean;
  removeBackgrounds: boolean;
  numberToRemove: number;
  applyWatermarks: boolean;
  candidatesPending: number;
}

export interface IListingRefreshPreviewException {
  partDataId: string;
  tagNumber: string;
  title: string | null;
  reason: string;
}

export interface IListingRefreshPreview {
  candidateCount: number;
  byState: IListingRefreshPreviewStates;
  activeEbayListingCount: number | null;
  imageSettings: IListingRefreshPreviewImageSettings;
  sampleExceptions: IListingRefreshPreviewException[];
}

export interface IListingRefreshBatchSummary {
  id: string;
  created: string;
  createdBy: string | null;
  isComplete: boolean;
  isPaused: boolean;
  isCancelled: boolean;
  total: number;
  done: number;
  failed: number;
  lastActivityOn: string | null;
  publishPerCycle: number;
  cycleMinutes: number;
}

export interface IListingRefreshEnrichmentCounts {
  pending: number;
  processing: number;
  done: number;
  failed: number;
}

export interface IListingRefreshCounts {
  total: number;
  enrichment: IListingRefreshEnrichmentCounts;
  markedEnded: number;
  readyToPublish: number;
  publishQueued: number;
  live: number;
  publishFailed: number;
  exceptions: number;
  cancelled: number;
}

// Mirrors GPMetals.Core.Entities.ListingRefreshStep, in pipeline order. The keys are what the API
// uses in stepStates; the enum values are what its query strings and retry bodies take.
export const LISTING_REFRESH_STEPS = [
  "aspects",
  "policies",
  "title",
  "price",
  "description",
  "imageSettings",
  "ktype",
  "markEnded",
  "readiness",
  "publish",
] as const;

export type ListingRefreshStep = typeof LISTING_REFRESH_STEPS[number];

export const LISTING_REFRESH_STEP_ENUM: Record<ListingRefreshStep, number> = {
  title: 0,
  price: 1,
  policies: 2,
  aspects: 3,
  description: 4,
  imageSettings: 5,
  ktype: 6,
  markEnded: 7,
  readiness: 8,
  publish: 9,
};

export const listingRefreshStepLabel: Record<ListingRefreshStep, string> = {
  aspects: "Aspects",
  policies: "Policies / location",
  title: "AI title",
  price: "Pricing",
  description: "Description",
  imageSettings: "Image settings",
  ktype: "KType",
  markEnded: "Mark ended",
  readiness: "Readiness",
  publish: "Publish",
};

export type IListingRefreshStepFailures = Record<ListingRefreshStep, number>;

export interface IListingRefreshMarkEndedState {
  requestedOn: string | null;
  approvedOn: string | null;
  activeListingCount: number | null;
  override: boolean;
}

export interface IListingRefreshBatchStatus {
  id: string;
  options: IListingRefreshOptions;
  isMaterialised: boolean;
  isComplete: boolean;
  isPaused: boolean;
  isCancelled: boolean;
  startedOn: string | null;
  lastActivityOn: string | null;
  enrichmentPausedUntil: string | null;
  etaMinutes: number | null;
  counts: IListingRefreshCounts;
  perStepFailures: IListingRefreshStepFailures;
  lastPublishCorrelationIds: string[];
  markEnded: IListingRefreshMarkEndedState;
}

// Mirrors GPMetals.Core.Entities.ListingRefreshStage.
export enum ListingRefreshStage {
  Pending = 0,
  Enriching = 1,
  Enriched = 2,
  MarkedEnded = 3,
  PublishQueued = 4,
  Live = 5,
  PublishFailed = 6,
  Exception = 7,
  Cancelled = 8,
}

export const listingRefreshStageLabel: Record<ListingRefreshStage, string> = {
  [ListingRefreshStage.Pending]: "Pending",
  [ListingRefreshStage.Enriching]: "Enriching",
  [ListingRefreshStage.Enriched]: "Enriched",
  [ListingRefreshStage.MarkedEnded]: "Marked ended",
  [ListingRefreshStage.PublishQueued]: "Publish queued",
  [ListingRefreshStage.Live]: "Live",
  [ListingRefreshStage.PublishFailed]: "Publish failed",
  [ListingRefreshStage.Exception]: "Exception",
  [ListingRefreshStage.Cancelled]: "Cancelled",
};

// Mirrors GPMetals.Core.Entities.ListingRefreshStepState.
export enum ListingRefreshStepState {
  NotRun = 0,
  Done = 1,
  Failed = 2,
  Skipped = 3,
}

export type ListingRefreshPublishState = "NotReady" | "Ready" | "Queued" | "Live" | "Failed" | "Excluded";

export interface IListingRefreshItemRow {
  id: string;
  partDataId: string;
  tagNumber: string;
  title: string | null;
  previousTitle: string | null;
  price: number | null;
  priceSource: number;
  stage: ListingRefreshStage;
  stepStates: Partial<Record<ListingRefreshStep, ListingRefreshStepState>>;
  error: string | null;
  errorStep: number | null;
  warning: string | null;
  publishState: ListingRefreshPublishState;
  marketplaces: string | null;
  previousEbayItemId: string | null;
  newSku: string | null;
  updated: string | null;
}

// Core's DefaultPaginated, as it serialises.
export interface IListingRefreshItemsPage {
  rows: IListingRefreshItemRow[];
  total: number;
  totalPages: number;
  page: number;
  pageSize: number;
}

export interface IListingRefreshItemsFilter {
  page: number;
  pageSize: number;
  stage: ListingRefreshStage | "";
  step: ListingRefreshStep | "";
  query: string;
}

export interface IListingRefreshRetryRequest {
  step?: number;
  partDataIds?: string[];
}

export interface IListingRefreshPublishRateRequest {
  publishPerCycle: number;
  cycleMinutes: number;
}

export interface IListingRefreshMarkEndedResult {
  activeEbayListingCount: number | null;
  refused: boolean;
  queued: number;
  alreadyConfirmedEndedByEbay: number;
  message: string;
}

export interface IListingRefreshActiveCount {
  activeEbayListingCount: number | null;
}
